// The session token store (contract 06 §5/§7) — the seam the shell's authGuard and the
// http composition root lean on. Holds the bearer (persisted via secure-ls), the MEMORY-ONLY
// MFA temp token + challenge state, the post-login redirect target, and the request-abort
// registry (v3's cancelTokens). `getToken()` is injected into shared/http at boot; auth never
// imports http.
import { defineStore } from 'pinia'
import { computed, ref } from 'vue'

import type { MfaChallenge, MfaChallengeState } from '../models/auth.types'

import { secureStorage } from './secureStorage'

export const useAuthStore = defineStore(
  // FROZEN: v3 persists under this id (`authStore.ts:13`). During the proxy split v3 and v4 serve
  // different routes on ONE origin and share this blob — a different id logs every user out at the
  // boundary. See sessionCutover.spec.ts before touching this or the persist options below.
  'authStore',
  () => {
    const token = ref<string | null>(null) // bearer — persisted (secure-ls)
    const tempToken = ref<string | null>(null) // MFA hand-off — MEMORY ONLY
    // Server-authored MFA challenge the /mfa screen renders (attempts/expiry/cooldown). MEMORY
    // ONLY, exactly like the temp token it travels with: a reload restarts the login leg anyway.
    const mfaChallenge = ref<MfaChallengeState | null>(null)
    const redirectTarget = ref<string | null>(null) // post-login route name — persisted
    // In-memory abort registry (v3 cancelTokens). NOT returned → never part of $state, never persisted.
    const abortHandles: Array<() => void> = []

    const isAuthenticated = computed(() => !!token.value) // contract 06 §7; empty string ⇒ unauthenticated

    // Ledger §C defect 6 (ORP-18613): install a real bearer only — a falsy value
    // (undefined/empty) is ignored, never stored or persisted. Clearing the session is
    // `reset()`'s job, not a falsy `setToken`. The legacy Shopify self-callback ran
    // `setToken(to.query.token)` on a branch that never gated on `?token`, wiping the session
    // to `undefined` (Orphex-New-Front/src/router/index.js:115); the ported callback seam
    // (ORP-18611) must not reproduce it, and this store refuses the value either way.
    function setToken(next: string | null): void {
      if (!next) return
      token.value = next
    }
    function setTempToken(next: string | null): void {
      tempToken.value = next
    }
    /** A challenge arrived (login / signup / resend): split it into its two owners at one site. */
    function startMfaChallenge(challenge: MfaChallenge): void {
      const { temp_token, ...state } = challenge
      tempToken.value = temp_token
      mfaChallenge.value = state
    }
    /** Replace the rendered challenge state — server-authored only (never a client guess). */
    function setMfaChallenge(next: MfaChallengeState | null): void {
      mfaChallenge.value = next
    }
    function rememberTarget(name: string | null): void {
      redirectTarget.value = name
    }
    function getToken(): string | null {
      return token.value
    }

    /** Register an abort handle; returns an unregister fn (v3 addCancelToken). */
    function registerAbort(fn: () => void): () => void {
      abortHandles.push(fn)
      return () => {
        const index = abortHandles.indexOf(fn)
        if (index !== -1) abortHandles.splice(index, 1)
      }
    }
    /** Abort every in-flight request (v3 cancelAllTokens); crash-safe per handle. */
    function abortAll(): void {
      const handles = abortHandles.splice(0)
      for (const abort of handles) {
        try {
          abort()
        } catch {
          /* one request's teardown must not block the rest */
        }
      }
    }
    /** Logout / 401 / handoff-swap reset. Clears auth only; other stores use the reset registry. */
    function reset(): void {
      token.value = null
      tempToken.value = null
      mfaChallenge.value = null
      redirectTarget.value = null
      abortAll()
    }

    return {
      token,
      tempToken,
      mfaChallenge,
      redirectTarget,
      isAuthenticated,
      setToken,
      setTempToken,
      startMfaChallenge,
      setMfaChallenge,
      rememberTarget,
      getToken,
      registerAbort,
      abortAll,
      reset,
    }
  },
  // secure-ls storage; ONLY the bearer + redirect target persist. tempToken/mfaChallenge/
  // abortHandles never do.
  // `key` is FROZEN to v3's resolved key: v3 sets no key, so persistedstate defaults it to the
  // store id `authStore`. Pinned explicitly here so an id rename cannot silently move the blob.
  // `pick` is load-bearing in BOTH directions: it keeps tempToken/mfaChallenge off disk, and it
  // drops v3's extra keys (tempToken/storeUrl/cancelTokens) when hydrating a v3-written blob.
  { persist: { key: 'authStore', pick: ['token', 'redirectTarget'], storage: secureStorage } },
)

export type AuthStore = ReturnType<typeof useAuthStore>
